import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { cotizar, rutaPorId, TARIFAS } from "../shared/tarifas";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* Tarifas de Fueguino: la escala de la carta de TransAmerica y un cotizador.
 *
 * ── QUÉ MUESTRA ────────────────────────────────────────────────────────────
 *
 * Arriba, un cotizador: elegís ruta, kilos y metros cúbicos y da el precio con
 * el desglose (de dónde sale cada peso). Abajo, la escala fiel de la carta —los
 * tramos por ruta, la tarifa por m³, el corte de carta de porte y la
 * bonificación—, para consultar sin calcular.
 *
 * ── LA ESCALA ES FIEL, LA REGLA ES BORRADOR ────────────────────────────────
 *
 * Los NÚMEROS salen tal cual de la carta (JR TRANS, vigencia 01/06/2026). Cómo
 * se COMBINAN para dar el precio —mínimo fijo del primer tramo, $/kg en el
 * resto, el mayor entre peso y volumen, menos 5%, más el corte— es una regla
 * razonable pero sin confirmar por Matías. Por eso el cotizador va marcado como
 * borrador: cuando confirme cómo cotiza de verdad, se ajusta en `shared/tarifas`.
 */

const pesos = (n) =>
  n === null || n === undefined
    ? "—"
    : Number(n).toLocaleString("es-AR", {
        style: "currency",
        currency: "ARS",
        maximumFractionDigits: 0,
      });

const kilos = (t, i, tramos) => {
  const desde = i === 0 ? 1 : tramos[i - 1].hasta + 1;
  if (t.hasta === Infinity) return `más de ${desde - 1} kg`;
  return `${desde} a ${t.hasta} kg`;
};

const FueguinoTarifas = () => {
  const { permisos = null } = useOutletContext() || {};
  const puedeVer = permisos !== null && permisos.includes("ver.encomiendas");

  const [rutaId, setRutaId] = useState(TARIFAS.rutas[1].id); // Río Grande por defecto
  const [kg, setKg] = useState("");
  const [m3, setM3] = useState("");

  const ruta = useMemo(() => rutaPorId(rutaId), [rutaId]);
  const coti = useMemo(
    () => (ruta && (kg || m3) ? cotizar(ruta, kg, m3) : null),
    [ruta, kg, m3],
  );

  if (permisos !== null && !puedeVer) {
    return <PuertaCerrada />;
  }

  return (
    <div className="docs">
      <h2 style={{ margin: "0 0 4px" }}>Tarifas · Fueguino</h2>
      <p className="sub">
        Escala de <b>{TARIFAS.emisor}</b> para <b>{TARIFAS.cliente}</b>, vigente
        desde el <b>01/06/2026</b>. Los precios no incluyen IVA.
      </p>

      <p className="aduana-error" style={{ background: "transparent" }}>
        ⚠️ <b>Cotizador en borrador.</b> La escala es fiel a la carta, pero la
        forma de combinarla (mínimo fijo, $/kg, el mayor entre peso y volumen,
        −5%, + corte) está <b>sin confirmar</b>. No la uses para cerrar precio
        hasta que se valide la regla.
      </p>

      {/* El cotizador */}
      <div className="tk-filtros" style={{ marginTop: 8 }}>
        <label className="sub" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Ruta
          <select value={rutaId} onChange={(e) => setRutaId(e.target.value)} aria-label="Ruta">
            {TARIFAS.rutas.map((r) => (
              <option key={r.id} value={r.id}>{r.nombre}</option>
            ))}
          </select>
        </label>
        <label className="sub" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Kg
          <input
            type="number" min="0" value={kg}
            onChange={(e) => setKg(e.target.value)}
            placeholder="0" aria-label="Kilos" style={{ width: 90 }}
          />
        </label>
        <label className="sub" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          m³
          <input
            type="number" min="0" step="0.01" value={m3}
            onChange={(e) => setM3(e.target.value)}
            placeholder="0" aria-label="Metros cúbicos" style={{ width: 90 }}
          />
        </label>
      </div>

      {coti && (
        <div className="tabla-wrap" style={{ marginTop: 10 }}>
          <table className="tabla">
            <tbody>
              <tr>
                <td>Por peso ({coti.tramo.fijo ? "mínimo fijo" : `${pesos(coti.tramo.precio)}/kg`})</td>
                <td className="dato">{pesos(coti.porPeso)}</td>
              </tr>
              <tr>
                <td>Por volumen ({pesos(ruta.tarifaM3)}/m³)</td>
                <td className="dato">{pesos(coti.porVolumen)}</td>
              </tr>
              <tr>
                <td>Flete (manda {coti.base === "peso" ? "el peso" : "el volumen"})</td>
                <td className="dato">{pesos(coti.flete)}</td>
              </tr>
              <tr>
                <td>Bonificación (−5%)</td>
                <td className="dato">−{pesos(coti.bonificacion)}</td>
              </tr>
              <tr>
                <td>Corte de carta de porte</td>
                <td className="dato">{pesos(coti.corteCartaPorte)}</td>
              </tr>
              <tr>
                <td><b>Total (sin IVA)</b></td>
                <td className="dato"><b>{pesos(coti.total)}</b></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* La escala, ruta por ruta */}
      <h3 style={{ margin: "22px 0 6px" }}>La escala</h3>
      {TARIFAS.rutas.map((r) => (
        <div key={r.id} style={{ marginBottom: 16 }}>
          <p className="sub" style={{ margin: "0 0 4px" }}>
            <b>{r.nombre}</b> · por m³: {pesos(r.tarifaM3)}
          </p>
          <div className="tabla-wrap">
            <table className="tabla">
              <thead>
                <tr><th>Tramo</th><th>Precio</th></tr>
              </thead>
              <tbody>
                {r.tramos.map((t, i) => (
                  <tr key={i}>
                    <td>{kilos(t, i, r.tramos)}</td>
                    <td className="dato">
                      {pesos(t.precio)}{t.fijo ? " (fijo)" : "/kg"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <p className="docs-pie">
        Corte de carta de porte: <b>{pesos(TARIFAS.corteCartaPorte)}</b> por guía ·
        Bonificación: <b>5%</b> sobre la tarifa · Condición de pago:{" "}
        {TARIFAS.condicionPago}. Los precios no incluyen IVA.
      </p>
    </div>
  );
};

export default FueguinoTarifas;
